/**
 * Built-in plugin registry — the first-party AudioWorklet plugins that ship
 * with ELPM Studio. Each entry exposes:
 *
 *   id          — stable internal slug
 *   label       — display name
 *   url         — public URL. FREE plugins are served as static files at
 *                 /plugins/<file>.js; PRO plugins are served through the
 *                 backend route /api/plugins/pro/<slug> which enforces an
 *                 auth + plan check server-side (preventing devtools bypass
 *                 of the client-side gate). AudioWorklet.addModule()'s default
 *                 credentials:"same-origin" lets the cookie flow through.
 *   processorName — the name registered via registerProcessor(...) inside the
 *                   worklet file.
 *   pro         — true if Pro-only. UI shows lock badge AND the URL above
 *                 is auth-protected.
 *   params      — schema for the param picker UI in PluginManagerPanel.
 */

// Pro plugin URLs are routed through the backend's REACT_APP_BACKEND_URL so
// the session_token cookie travels with the AudioWorklet.addModule() fetch.
const API = process.env.REACT_APP_BACKEND_URL || "";

export const BUILT_IN_PLUGINS = [
  {
    id: "bitcrusher",
    label: "Bitcrusher · Sync",
    description: "Bit-depth reducer with tempo-locked sample-hold. Locks to the DAW's beat division so it stays musical at any BPM.",
    url: "/plugins/bitcrusher-processor.js",
    processorName: "bitcrusher-processor",
    params: [
      { key: "bits",    label: "Bits",         min: 1,  max: 16, step: 1,    defaultValue: 8,  unit: "bit",  kind: "slider" },
      { key: "beatDiv", label: "Beat Division", min: 1, max: 32, step: 1,    defaultValue: 4,  unit: "/beat", kind: "slider" },
      { key: "mix",     label: "Mix",          min: 0,  max: 1,  step: 0.01, defaultValue: 1,  unit: "",     kind: "slider" },
    ],
  },
  {
    id: "stutter",
    label: "Stutter Gate · Sync",
    description: "Rhythmic gate that opens and closes in time with the host clock. Great on vocals + percussive bus.",
    url: "/plugins/stutter-processor.js",
    processorName: "stutter-processor",
    params: [
      { key: "beatDiv", label: "Beat Division", min: 1, max: 32, step: 1,    defaultValue: 2,  unit: "/beat", kind: "slider" },
      { key: "duty",    label: "Duty Cycle",   min: 0.05, max: 0.95, step: 0.01, defaultValue: 0.5, unit: "", kind: "slider" },
      { key: "mix",     label: "Mix",          min: 0,  max: 1,  step: 0.01, defaultValue: 1,  unit: "",     kind: "slider" },
    ],
  },
  {
    id: "ringmod",
    label: "Ring Mod · Sync",
    description: "Frequency-mod carrier whose pitch is locked to the host tempo. Set beatDiv=0 to free-run at any Hz.",
    url: "/plugins/ringmod-processor.js",
    processorName: "ringmod-processor",
    params: [
      { key: "beatDiv", label: "Beat Multiplier", min: 0, max: 64, step: 1, defaultValue: 16, unit: "× tempo", kind: "slider" },
      { key: "freeHz",  label: "Free Hz (if div=0)", min: 1, max: 2000, step: 1, defaultValue: 220, unit: "Hz", kind: "slider" },
      { key: "mix",     label: "Mix",          min: 0,  max: 1,  step: 0.01, defaultValue: 0.7, unit: "",    kind: "slider" },
    ],
  },
  {
    id: "pumper",
    label: "Pumper · Sync",
    description: "Beat-locked sidechain ducker that creates that classic dance-music pumping effect — without needing a separate sidechain input.",
    url: `${API}/api/plugins/pro/pumper`,
    processorName: "pumper-processor",
    pro: true,
    params: [
      { key: "beatDiv", label: "Pump Rate",   min: 1, max: 16, step: 1,    defaultValue: 1,    unit: "/beat", kind: "slider" },
      { key: "depth",   label: "Depth",       min: 0, max: 1,  step: 0.01, defaultValue: 0.25, unit: "",      kind: "slider" },
      { key: "release", label: "Release",     min: 0.1, max: 1, step: 0.01, defaultValue: 0.7, unit: "",      kind: "slider" },
      { key: "mix",     label: "Mix",         min: 0, max: 1,  step: 0.01, defaultValue: 1,    unit: "",      kind: "slider" },
    ],
  },
  {
    id: "multiband",
    label: "Multiband Comp · Pro",
    description: "Studio-grade 3-band compressor (low/mid/high). Glue and shape your mix without smearing transients.",
    url: `${API}/api/plugins/pro/multiband`,
    processorName: "multiband-processor",
    pro: true,
    params: [
      { key: "threshold", label: "Threshold", min: 0, max: 1,    step: 0.01, defaultValue: 0.6, unit: "",  kind: "slider" },
      { key: "ratio",     label: "Ratio",     min: 1, max: 20,   step: 0.5,  defaultValue: 4,   unit: ":1", kind: "slider" },
      { key: "lowGain",   label: "Low Gain",  min: 0, max: 2,    step: 0.01, defaultValue: 1,   unit: "×", kind: "slider" },
      { key: "midGain",   label: "Mid Gain",  min: 0, max: 2,    step: 0.01, defaultValue: 1,   unit: "×", kind: "slider" },
      { key: "highGain",  label: "High Gain", min: 0, max: 2,    step: 0.01, defaultValue: 1,   unit: "×", kind: "slider" },
      { key: "mix",       label: "Mix",       min: 0, max: 1,    step: 0.01, defaultValue: 1,   unit: "",  kind: "slider" },
    ],
  },
  {
    id: "tapesat",
    label: "Tape Saturator · Sync",
    description: "Analog tape warmth with tempo-locked wow + flutter modulation. Adds harmonic richness + subtle musical pitch drift.",
    url: `${API}/api/plugins/pro/tapesat`,
    processorName: "tapesat-processor",
    pro: true,
    params: [
      { key: "drive",   label: "Drive",         min: 0.1, max: 10,  step: 0.1,  defaultValue: 1.5, unit: "×",     kind: "slider" },
      { key: "warmth",  label: "Warmth",        min: 0,   max: 1,   step: 0.01, defaultValue: 0.4, unit: "",      kind: "slider" },
      { key: "wow",     label: "Wow Depth",     min: 0,   max: 15,  step: 0.1,  defaultValue: 4,   unit: "ms",    kind: "slider" },
      { key: "wowBeat", label: "Wow Rate",      min: 0.25, max: 16, step: 0.25, defaultValue: 1,   unit: "/beat", kind: "slider" },
      { key: "flutter", label: "Flutter Depth", min: 0,   max: 3,   step: 0.05, defaultValue: 0.3, unit: "ms",    kind: "slider" },
      { key: "mix",     label: "Mix",           min: 0,   max: 1,   step: 0.01, defaultValue: 1,   unit: "",      kind: "slider" },
    ],
  },
  {
    id: "talkbox",
    label: "Talkbox · Sync",
    description: "Vowel-formant filter that rotates through a / e / i / o / u in time with the host. Optional robot-mode carrier for a vocoder-lite timbre.",
    url: `${API}/api/plugins/pro/talkbox`,
    processorName: "talkbox-processor",
    pro: true,
    params: [
      { key: "rate",    label: "Vowel Rate",   min: 0.25, max: 16, step: 0.25, defaultValue: 4,   unit: "/beat", kind: "slider" },
      { key: "robot",   label: "Robot Blend",  min: 0,    max: 1,  step: 0.01, defaultValue: 0,   unit: "",      kind: "slider" },
      { key: "tone",    label: "Tone (Q)",     min: 0,    max: 1,  step: 0.01, defaultValue: 0.5, unit: "",      kind: "slider" },
      { key: "carrier", label: "Carrier Hz",   min: 1,    max: 880, step: 1,   defaultValue: 220, unit: "Hz",    kind: "slider" },
      { key: "mix",     label: "Mix",          min: 0,    max: 1,  step: 0.01, defaultValue: 1,   unit: "",      kind: "slider" },
    ],
  },
  {
    id: "modalreverb",
    label: "Modal Reverb · Pro",
    description: "Bank of musical resonators tuned to root + harmonics. Sympathetic ringing reverb that always stays in key.",
    url: `${API}/api/plugins/pro/modalreverb`,
    processorName: "modalreverb-processor",
    pro: true,
    params: [
      { key: "root",    label: "Root (Hz)",   min: 50,  max: 880, step: 1,    defaultValue: 220, unit: "Hz",  kind: "slider" },
      { key: "decay",   label: "Decay",       min: 0.1, max: 8,   step: 0.1,  defaultValue: 2.5, unit: "s",   kind: "slider" },
      { key: "spread",  label: "Spread (Q)",  min: 0,   max: 1,   step: 0.01, defaultValue: 0.4, unit: "",    kind: "slider" },
      { key: "damping", label: "Damping",     min: 0,   max: 1,   step: 0.01, defaultValue: 0.4, unit: "",    kind: "slider" },
      { key: "mix",     label: "Mix",         min: 0,   max: 1,   step: 0.01, defaultValue: 0.4, unit: "",    kind: "slider" },
    ],
  },
  {
    id: "phaser",
    label: "Phaser · Sync",
    description: "Classic 2..8-stage all-pass phaser sweep, tempo-locked LFO. From subtle shimmer to dramatic wobble.",
    url: `${API}/api/plugins/pro/phaser`,
    processorName: "phaser-processor",
    pro: true,
    params: [
      { key: "rate",     label: "Rate",       min: 0.125, max: 16, step: 0.125, defaultValue: 1,   unit: "/beat", kind: "slider" },
      { key: "depth",    label: "Depth",      min: 0,     max: 1,  step: 0.01,  defaultValue: 0.7, unit: "",      kind: "slider" },
      { key: "feedback", label: "Feedback",   min: 0,     max: 0.9, step: 0.01, defaultValue: 0.5, unit: "",      kind: "slider" },
      { key: "stages",   label: "Stages",     min: 2,     max: 8,  step: 1,     defaultValue: 4,   unit: "",      kind: "slider" },
      { key: "centerHz", label: "Center Hz",  min: 50,    max: 8000, step: 10,  defaultValue: 800, unit: "Hz",    kind: "slider" },
      { key: "mix",      label: "Mix",        min: 0,     max: 1,  step: 0.01,  defaultValue: 0.5, unit: "",      kind: "slider" },
    ],
  },
  // iter-156 — Waves-alternative starter pack: SSL Buss Comp + Pultec EQ + Vintage Tape.
  // All three are FREE so every user gets the "magic glue" mastering chain
  // available on every channel out of the box.
  {
    id: "sslcomp",
    label: "SSL Buss Comp",
    description: "Stereo-linked feedback compressor in the SSL G-Comp tradition. The 'magic glue' compressor used on countless pop/rock masters.",
    url: "/plugins/sslcomp-processor.js",
    processorName: "sslcomp-processor",
    params: [
      { key: "threshold", label: "Threshold", min: -40, max: 0,    step: 0.5,  defaultValue: -10, unit: "dB",  kind: "slider" },
      { key: "ratio",     label: "Ratio",     min: 1.5, max: 10,   step: 0.5,  defaultValue: 4,   unit: ":1",  kind: "slider" },
      { key: "attack",    label: "Attack",    min: 0.1, max: 30,   step: 0.1,  defaultValue: 10,  unit: "ms",  kind: "slider" },
      { key: "release",   label: "Release",   min: 50,  max: 1200, step: 1,    defaultValue: 250, unit: "ms",  kind: "slider" },
      { key: "knee",      label: "Knee",      min: 0,   max: 12,   step: 0.5,  defaultValue: 2,   unit: "dB",  kind: "slider" },
      { key: "makeup",    label: "Makeup",    min: -12, max: 24,   step: 0.5,  defaultValue: 0,   unit: "dB",  kind: "slider" },
      { key: "mix",       label: "Mix",       min: 0,   max: 1,    step: 0.01, defaultValue: 1,   unit: "",    kind: "slider" },
    ],
  },
  {
    id: "pultec",
    label: "Pultec EQ",
    description: "Vintage passive program EQ inspired by the EQP-1A. The legendary 'Pultec trick' — boost AND cut at the same low freq for warm-and-tight low end.",
    url: "/plugins/pultec-processor.js",
    processorName: "pultec-processor",
    params: [
      { key: "lowBoostFreq",  label: "Low Freq",    min: 20,   max: 100,   step: 1,    defaultValue: 60,    unit: "Hz", kind: "slider" },
      { key: "lowBoost",      label: "Low Boost",   min: 0,    max: 13.5,  step: 0.1,  defaultValue: 3,     unit: "dB", kind: "slider" },
      { key: "lowCut",        label: "Low Cut",     min: 0,    max: 18,    step: 0.1,  defaultValue: 0,     unit: "dB", kind: "slider" },
      { key: "highBoostFreq", label: "High Freq",   min: 3000, max: 16000, step: 100,  defaultValue: 10000, unit: "Hz", kind: "slider" },
      { key: "highBoost",     label: "High Boost",  min: 0,    max: 18,    step: 0.1,  defaultValue: 4,     unit: "dB", kind: "slider" },
      { key: "highCutFreq",   label: "Atten Freq",  min: 5000, max: 20000, step: 100,  defaultValue: 10000, unit: "Hz", kind: "slider" },
      { key: "highCut",       label: "Atten",       min: 0,    max: 16,    step: 0.1,  defaultValue: 0,     unit: "dB", kind: "slider" },
      { key: "mix",           label: "Mix",         min: 0,    max: 1,     step: 0.01, defaultValue: 1,     unit: "",   kind: "slider" },
    ],
  },
  {
    id: "vintapeglue",
    label: "Vintage Tape",
    description: "1-inch tape simulation at 15 ips — soft-knee saturation + wow/flutter + tone-shaping. The analog warmth that glues a mix together.",
    url: "/plugins/vintapeglue-processor.js",
    processorName: "vintapeglue-processor",
    params: [
      { key: "drive",   label: "Drive",    min: 1,   max: 4,   step: 0.05, defaultValue: 1.5, unit: "×",  kind: "slider" },
      { key: "bias",    label: "Bias",     min: 0,   max: 1,   step: 0.01, defaultValue: 0.5, unit: "",   kind: "slider" },
      { key: "tone",    label: "Tone",     min: -6,  max: 6,   step: 0.1,  defaultValue: 0,   unit: "dB", kind: "slider" },
      { key: "wow",     label: "Wow",      min: 0,   max: 1,   step: 0.01, defaultValue: 0.15, unit: "",  kind: "slider" },
      { key: "flutter", label: "Flutter",  min: 0,   max: 1,   step: 0.01, defaultValue: 0.10, unit: "",  kind: "slider" },
      { key: "mix",     label: "Mix",      min: 0,   max: 1,   step: 0.01, defaultValue: 1,   unit: "",   kind: "slider" },
    ],
  },
];

export function getBuiltInById(id) {
  return BUILT_IN_PLUGINS.find((p) => p.id === id) || null;
}
